/**
 * Label Style Controls Component
 * Allows users to customize the appearance of widget labels
 */

import React from 'react';
import { getWidgetStyleTargets, hasLabelDefinitions, getDefaultStyles } from '../../../widgets/widgetLabelHelpers';
import { formatLabelName } from '../../../widgets/widgetLabelDefinitions';
import { resolveCategoryStatusColor } from '../../../widgets/dynamicColorResolver';

const CATEGORIES = [
    // Localhost Scan
    { label: 'Localhost: Antivirus', value: 'Antivirus' },
    { label: 'Localhost: OS', value: 'OS (Localhost Scan)' },
    { label: 'Localhost: Services', value: 'Services (Localhost Scan)' },
    { label: 'Localhost: Hardware', value: 'Hardware' },
    { label: 'Localhost: Softwares', value: 'Softwares' },
    { label: 'Localhost: Drive', value: 'Drive' },
    { label: 'Localhost: Age', value: 'Age' },
    { label: 'Localhost: Open Ports', value: 'Open Ports (Localhost Scan)' },
    
    // Network Scan
    { label: 'Network: Backups', value: 'Backups' },
    { label: 'Network: Services', value: 'Services (Network Scan)' },
    { label: 'Network: OS', value: 'OS (Network Scan)' },
    { label: 'Network: Firewall', value: 'Firewall (Network Scan)' },
    { label: 'Network: Open Ports', value: 'Open Ports (Network Scan)' },
    { label: 'Network: Encryption', value: 'Encryption' },
    { label: 'Network: Antivirus', value: 'Antivirus (Network Scan)' },
    { label: 'Network: Vulnerabilities', value: 'Vulnerabilities' },
    
    // AD Scan
    { label: 'AD: Admins', value: 'Admins' },
    { label: 'AD: Computers', value: 'AD Computers' },
    { label: 'AD: Users', value: 'AD Users' },
    { label: 'AD: OUs', value: 'OU' },
    { label: 'AD: Groups', value: 'AD Groups' },
    { label: 'AD: Password Policy', value: 'Password Policy' },
    { label: 'AD: GPO Analysis', value: 'GPO Analysis' },
    
    // MS365 Scan
    { label: 'MS365: Users', value: 'MS Users' },
    { label: 'MS365: MFA', value: 'MFA' },
    { label: 'MS365: Policy', value: 'MS Policy' },
    { label: 'MS365: Secure Score', value: 'Secure Score' },
    
    // PII Scan
    { label: 'PII: Detection', value: 'PII' },
    { label: 'PII: Data Privacy', value: 'Data Privacy' },
    
    // Dark Web
    { label: 'Dark Web', value: 'Dark Web' }
];

export const LabelStyleControls = ({ block, updateConfig, dataContext, theme }) => {
    // Only show for widgets that have label definitions
    if (block.type !== 'widget' || !hasLabelDefinitions(block.widgetType)) {
        return null;
    }

    const styleTargets = getWidgetStyleTargets(block.widgetType);

    // No style targets defined for this widget
    if (styleTargets.length === 0) {
        return null;
    }

    const updateLabelStyle = (target, property, value) => {
        const newLabelStyles = {
            ...block.config.labelStyles,
            [target]: {
                ...block.config.labelStyles?.[target],
                [property]: value
            }
        };
        updateConfig('labelStyles', newLabelStyles);
    };

    const resetLabelStyle = (target) => {
        const newLabelStyles = { ...block.config.labelStyles };
        delete newLabelStyles[target];
        updateConfig('labelStyles', newLabelStyles);
    };

    return (
        <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb' }}>
            <h4 style={{ margin: '0 0 1rem 0', fontSize: '15px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Text Styles
            </h4>
            
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '1.25rem', fontStyle: 'italic' }}>
                Customize the appearance of titles, headers, and rows for this widget.
            </div>

            {styleTargets.map((target) => {
                const defaultStyles = getDefaultStyles(block.widgetType, target);
                const currentStyles = block.config.labelStyles?.[target] || {};
                const isTitle = target === 'title' || target === 'Widget Title';

                return (
                    <div
                        key={target}
                        className="property-subgroup"
                        style={{
                            marginBottom: '16px',
                            padding: '12px',
                            backgroundColor: '#f9fafb',
                            borderRadius: '8px',
                            border: '1px solid #f3f4f6'
                        }}
                    >
                        <div style={{ 
                            fontWeight: '700', 
                            marginBottom: '12px', 
                            fontSize: '13px', 
                            color: '#111827',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                            <span>{formatLabelName(target)}</span>
                            {Object.keys(currentStyles).length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => resetLabelStyle(target)}
                                    style={{ 
                                        border: 'none', 
                                        background: 'none', 
                                        color: '#ef4444', 
                                        fontSize: '10px', 
                                        padding: '0',
                                        cursor: 'pointer',
                                        fontWeight: '500'
                                    }}
                                >
                                    Reset
                                </button>
                            )}
                        </div>

                        <div style={{ marginBottom: '12px' }}>
                            {/* Color */}
                            <div className="property-field">
                                <label>Text Color</label>
                                <div style={{ display: 'flex', gap: '4px', flexDirection: 'column' }}>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        {(() => {
                                            let displayColor = currentStyles.color;
                                            let isDynamic = false;
                                            
                                            if (currentStyles.color?.startsWith('dynamic:statusColor:')) {
                                                const category = currentStyles.color.split(':')[2];
                                                displayColor = resolveCategoryStatusColor(category, dataContext, theme);
                                                isDynamic = true;
                                            } else if (!currentStyles.color) {
                                                displayColor = defaultStyles.color || '#000000';
                                            }

                                            return (
                                                <>
                                                    <input
                                                        type="color"
                                                        value={displayColor.startsWith('#') ? displayColor : '#cccccc'}
                                                        onChange={(e) => updateLabelStyle(target, 'color', e.target.value)}
                                                        disabled={isDynamic}
                                                        style={{ width: '40px', height: '32px', padding: '2px', borderRadius: '4px', border: '1px solid #d1d5db', cursor: isDynamic ? 'not-allowed' : 'pointer', opacity: isDynamic ? 0.7 : 1 }}
                                                        title={isDynamic ? `Dynamic color from ${currentStyles.color.split(':')[2]}` : 'Static color'}
                                                    />
                                                    <input
                                                        type="text"
                                                        value={currentStyles.color || ''}
                                                        onChange={(e) => updateLabelStyle(target, 'color', e.target.value)}
                                                        placeholder={displayColor}
                                                        style={{ flex: 1, height: '32px', fontSize: '12px', border: isDynamic ? '1px dashed #3b82f6' : '1px solid #d1d5db' }}
                                                        title={isDynamic ? `Currently resolving to: ${displayColor}` : ''}
                                                    />
                                                </>
                                            );
                                        })()}
                                    </div>
                                    <select
                                        style={{ fontSize: '13px', height: '34px', marginTop: '4px', width: '100%', padding: '0 8px' }}
                                        value={currentStyles.color?.startsWith('dynamic:statusColor:') ? currentStyles.color.split(':')[2] : ''}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (val) {
                                                updateLabelStyle(target, 'color', `dynamic:statusColor:${val}`);
                                            } else if (currentStyles.color?.startsWith('dynamic:')) {
                                                updateLabelStyle(target, 'color', undefined);
                                            }
                                        }}
                                    >
                                        <option value="">-- Static Color --</option>
                                        <optgroup label="Link to Category Status">
                                            {CATEGORIES.map(cat => (
                                                <option key={cat.value} value={cat.value}>{cat.label}</option>
                                            ))}
                                        </optgroup>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            {/* Font Size */}
                            <div className="property-field">
                                <label>Font Size (px)</label>
                                <input
                                    type="number"
                                    value={currentStyles.fontSize ? parseInt(currentStyles.fontSize.replace('px', '')) : ''}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val) {
                                            updateLabelStyle(target, 'fontSize', `${val}px`);
                                        } else {
                                            updateLabelStyle(target, 'fontSize', undefined);
                                        }
                                    }}
                                    placeholder={defaultStyles.fontSize ? defaultStyles.fontSize.replace('px', '') : '14'}
                                    style={{ width: '100%' }}
                                />
                            </div>

                            {/* Font Weight */}
                            <div className="property-field">
                                <label>Weight</label>
                                <select
                                    value={currentStyles.fontWeight || ''}
                                    onChange={(e) => updateLabelStyle(target, 'fontWeight', e.target.value)}
                                    style={{ width: '100%', height: '34px', fontSize: '13px', padding: '0 8px' }}
                                >
                                    <option value="">Default</option>
                                    <option value="300">Light</option>
                                    <option value="400">Normal</option>
                                    <option value="500">Medium</option>
                                    <option value="600">Semi-Bold</option>
                                    <option value="700">Bold</option>
                                </select>
                            </div>

                            {/* Horizontal Align */}
                            {target !== 'auditScoreValue' && (
                                <div className="property-field">
                                    <label>Text Align</label>
                                    <select
                                        value={currentStyles.textAlign || ''}
                                        onChange={(e) => updateLabelStyle(target, 'textAlign', e.target.value)}
                                        style={{ width: '100%', height: '34px', fontSize: '13px', padding: '0 8px' }}
                                    >
                                        <option value="">Default</option>
                                        <option value="left">Left</option>
                                        <option value="center">Center</option>
                                        <option value="right">Right</option>
                                    </select>
                                </div>
                            )}
                        </div>

                        {/* Background Color */}
                        {target !== 'auditScoreValue' && (
                            <div className="property-field" style={{ marginTop: '12px' }}>
                                <label>Background Color</label>
                                <div style={{ display: 'flex', gap: '4px', flexDirection: 'column' }}>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        {(() => {
                                            let displayColor = currentStyles.backgroundColor;
                                            let isDynamic = false;
                                            
                                            if (currentStyles.backgroundColor?.startsWith('dynamic:statusColor:')) {
                                                const category = currentStyles.backgroundColor.split(':')[2];
                                                displayColor = resolveCategoryStatusColor(category, dataContext, theme);
                                                isDynamic = true;
                                            } else if (!currentStyles.backgroundColor) {
                                                displayColor = defaultStyles.backgroundColor || 'transparent';
                                            }

                                            const colorValue = displayColor === 'transparent' ? '#ffffff' : (displayColor.startsWith('#') ? displayColor : '#cccccc');

                                            return (
                                                <>
                                                    <input
                                                        type="color"
                                                        value={colorValue}
                                                        onChange={(e) => updateLabelStyle(target, 'backgroundColor', e.target.value)}
                                                        disabled={isDynamic}
                                                        style={{ width: '40px', height: '32px', padding: '2px', borderRadius: '4px', border: '1px solid #d1d5db', cursor: isDynamic ? 'not-allowed' : 'pointer', opacity: isDynamic ? 0.7 : 1 }}
                                                        title={isDynamic ? `Dynamic background color from ${currentStyles.backgroundColor.split(':')[2]}` : 'Static background color'}
                                                    />
                                                    <input
                                                        type="text"
                                                        value={currentStyles.backgroundColor || ''}
                                                        onChange={(e) => updateLabelStyle(target, 'backgroundColor', e.target.value)}
                                                        placeholder={displayColor}
                                                        style={{ flex: 1, height: '32px', fontSize: '12px', border: isDynamic ? '1px dashed #3b82f6' : '1px solid #d1d5db' }}
                                                        title={isDynamic ? `Currently resolving to: ${displayColor}` : ''}
                                                    />
                                                    {currentStyles.backgroundColor && (
                                                        <button
                                                            type="button"
                                                            onClick={() => updateLabelStyle(target, 'backgroundColor', undefined)}
                                                            style={{ 
                                                                border: 'none', 
                                                                background: 'none', 
                                                                color: '#6b7280', 
                                                                fontSize: '18px', 
                                                                padding: '0 4px',
                                                                cursor: 'pointer'
                                                            }}
                                                            title="Clear background"
                                                        >
                                                            ×
                                                        </button>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </div>
                                    <select
                                        style={{ fontSize: '13px', height: '34px', marginTop: '4px', width: '100%', padding: '0 8px' }}
                                        value={currentStyles.backgroundColor?.startsWith('dynamic:statusColor:') ? currentStyles.backgroundColor.split(':')[2] : ''}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (val) {
                                                updateLabelStyle(target, 'backgroundColor', `dynamic:statusColor:${val}`);
                                            } else if (currentStyles.backgroundColor?.startsWith('dynamic:')) {
                                                updateLabelStyle(target, 'backgroundColor', undefined);
                                            }
                                        }}
                                    >
                                        <option value="">-- Static Color --</option>
                                        <optgroup label="Link to Category Status">
                                            {CATEGORIES.map(cat => (
                                                <option key={cat.value} value={cat.value}>{cat.label}</option>
                                            ))}
                                        </optgroup>
                                    </select>
                                </div>
                            </div>
                        )}


                        {/* Optional Vertical Align for certain targets */}
                        {(target === 'headers' || target === 'rows') && (
                            <div className="property-field" style={{ marginTop: '8px' }}>
                                <label>Vertical Alignment</label>
                                <select
                                    value={currentStyles.verticalAlign || ''}
                                    onChange={(e) => updateLabelStyle(target, 'verticalAlign', e.target.value)}
                                    style={{ width: '100%', height: '34px', fontSize: '13px', padding: '0 8px' }}
                                >
                                    <option value="">Default</option>
                                    <option value="top">Top</option>
                                    <option value="middle">Middle</option>
                                    <option value="bottom">Bottom</option>
                                </select>
                            </div>
                        )}
                    </div>
                );
            })}
            
            {block.config.labelStyles && Object.keys(block.config.labelStyles).length > 0 && (
                <button
                    className="btn btn-outline-secondary btn-sm w-100"
                    onClick={() => updateConfig('labelStyles', {})}
                    style={{ marginTop: '8px', fontSize: '12px' }}
                >
                    Reset All Styles
                </button>
            )}
        </div>
    );
};

export default LabelStyleControls;
